// @ts-check
/* eslint no-param-reassign: "error" */

import { forEach, size, values } from "lodash";
import { nanoid } from "src/util/simpleId";
import { ADDED, REMOVED, MOVED } from "../diffStatus";
import {
  hashGrouping,
  newGrouping,
  newGroupingOptions,
  setCalculatedSumTotals,
  setGroupingsWithPlannerCells,
  setNotifications,
} from "./util";
import setDemandGroupingsWithSupplyLineItems from "./buildInitialModel/setLineItems/supplyLineItems";

const updateModelWithBaselineDiff = (
  model,
  groupDiffData,
  {
    groupTypes,
    isBudgetEnabled,
    showObjectives,
    useDeltaForBudget,
    isRealtimeFteMode,
    useRealtimeChangeOnly,
    useRealtimeFteDelta,
    rootGroup,
    hideHiddenTeams,
  }
) => {
  const {
    columns,
    groupings,
    lookups: {
      groupLookup,
      groupingLookup,
      allocationProjectLookup,
      macroAllocationLookup,
    },
  } = model;

  const activeAllocationProject = values(allocationProjectLookup).find(
    (ap) => ap.isActive
  );
  const rootGrouping = groupings?.[0];

  if (!activeAllocationProject || !rootGrouping) {
    return model;
  }

  const diffs = groupDiffData.filter((g) => g.diff);
  const adds = diffs.filter((g) => g.diff.status === ADDED);
  const moves = diffs.filter((g) => g.diff.status === MOVED);
  [...adds, ...moves].forEach((gd) => {
    // we should be able to find the movedGroup still
    const movedGroup = groupLookup[gd.id];

    // if not exit
    if (!movedGroup) {
      return;
    }

    const movedGrouping = groupingLookup[gd.id];
    const movedToGroup = groupLookup[movedGroup?.directParentId];
    const movedFromGroupId = gd.diff.group?.directParentId;
    const movedFromGrouping = groupingLookup[movedFromGroupId];

    if (movedGrouping) {
      // moved in
      movedGrouping.isMovedIn = true;
      movedGrouping.movedParent = gd.diff.group?.directParent;
      if (groupingLookup[gd.diff.group?.directParentId]) {
        // existing parent is still in the picture = internal move
        movedGrouping.isMovedInternally = true;
      }
    } else if (!hideHiddenTeams) {
      // haven haven't set hide hidden, so lets check
      // if this is a moved out group and add it in
      if (movedFromGrouping) {
        // moved out dance
        const movedOutGrouping = newGrouping({
          ...newGroupingOptions({
            group: movedGroup,
            groupType: groupTypes[movedGroup.type],
            parentGrouping: movedFromGrouping,
            activeAllocationProject,
            isBudgetEnabled,
            groupLookup: {},
          }),
          id: `${gd.id}-${nanoid()}virtual-moved-out-grouping`,
          parentGrouping: movedFromGrouping,
          isVirtual: true,
          rootGrouping,
          nestedLevel: movedFromGrouping.nestedLevel + 1,
          canAddLineItem: false,
          canViewInTeamBuilder: false,
          canViewInDetails: true,
          showObjectives,
          columnCount: size(columns),
          isMovedOut: true,
          movedParent: movedToGroup || {
            id: movedGroup.directParentId,
          },
        });
        setDemandGroupingsWithSupplyLineItems({
          groupings: [movedOutGrouping],
          macroAllocations: macroAllocationLookup,
          columnCount: columns.length,
          groupTypes,
          canRename: false,
          isBudgetEnabled: false,
          hideHiddenTeams: false,
          groupLookup,
        });
        setGroupingsWithPlannerCells({
          groupings: [movedOutGrouping],
          macroAllocations: macroAllocationLookup,
          allocationProjectLookup,
          isRequestor: !rootGroup.isSource,
          groupedByDemand: true,
          useDeltaForBudget,
          isRealtimeFteMode: rootGroup.isDemand && isRealtimeFteMode,
          useRealtimeChangeOnly: rootGroup.isDemand && useRealtimeChangeOnly,
          useRealtimeFteDelta: rootGroup.isDemand && useRealtimeFteDelta,
        });
        // TODO: need to recursively set child grouping
        movedFromGrouping.childGroupings.push(movedOutGrouping);
        groupingLookup[movedOutGrouping.id] = movedOutGrouping;
      }
    }
  });

  const removes = diffs.filter((g) => g.diff.status === REMOVED);
  forEach(removes, (g) => {
    // we should be able to find the grouping still
    const removedGrouping = groupingLookup[g.id];
    if (removedGrouping) {
      removedGrouping.isMovedOut = true;
      removedGrouping.movedParent = null;
      setGroupingsWithPlannerCells({
        groupings: [removedGrouping],
        macroAllocations: macroAllocationLookup,
        allocationProjectLookup,
        isRequestor: !rootGroup.isSource,
        groupedByDemand: true,
        useDeltaForBudget,
        isRealtimeFteMode: rootGroup.isDemand && isRealtimeFteMode,
        useRealtimeChangeOnly: rootGroup.isDemand && useRealtimeChangeOnly,
        useRealtimeFteDelta: rootGroup.isDemand && useRealtimeFteDelta,
      });
    }
  });

  setCalculatedSumTotals([rootGrouping], columns);
  setNotifications([rootGrouping], columns);
  hashGrouping(rootGrouping);
  return model;
};

export default updateModelWithBaselineDiff;
